import { useState, useRef, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import qs from "query-string";
import { client } from "../services/axiosClient";

const stripText = (text) => {
  return text.replace(/^\s+|\s+$|\s+(?=\s)/g, "");
};

const orderedScenarios = (scn) => {
  return [...scn].sort((a, b) => {
    const x = a.name;
    const y = b.name;
    return x < y ? -1 : x > y ? 1 : 0;
  });
};

/**
 * Centralized hook for scenario management (save, load, share, URL resolve).
 *
 * @param {Object} opts
 * @param {string} opts.viewType - "pl_explorer" | "monthly_pl"
 * @param {() => object} opts.buildPayload - returns current view state to serialize
 * @param {(data: object) => void} opts.applyPayload - applies deserialized state
 * @param {function} opts.t - i18n translate function
 * @param {function} opts.notify - toast notification function
 */
export function useScenarios({
  viewType,
  buildPayload,
  applyPayload,
  t,
  notify,
}) {
  const location = useLocation();

  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [nameModalInput, setNameModalInput] = useState("");
  const [saveMode, setSaveMode] = useState("");
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareURL, setShareURL] = useState("");
  const [shareLoading, setShareLoading] = useState(false);

  const nameInputRef = useRef(null);
  const urlHashRef = useRef(null);
  const initApplied = useRef(false);

  // Parse ?scenario=<hash> from URL on mount
  useEffect(() => {
    try {
      const params = qs.parse(location.search, { ignoreQueryPrefix: true });
      if (params.scenario) {
        urlHashRef.current = params.scenario;
      }
    } catch {
      // ignore parse errors — handled in initFromURL
    }
  }, []); // eslint-disable-line

  const filteredScenarios = useCallback(() => {
    return scenarios.filter((s) => s.view_type === viewType);
  }, [scenarios, viewType]);

  // Populate scenarios list from meta response
  const setScenariosFromMeta = useCallback((allScenarios) => {
    setScenarios(allScenarios || []);
  }, []);

  // Resolve ?scenario=<hash> after meta is loaded
  const initFromURL = useCallback(async () => {
    if (!urlHashRef.current || initApplied.current) return;
    initApplied.current = true;

    const hash = urlHashRef.current;
    setBusy(true);
    setLoading(true);

    try {
      const response = await client.get(`api/scenarios/resolve/${hash}/`);
      const data = response.data;
      const scenarioData = JSON.parse(data.scenario_data);
      applyPayload(scenarioData);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410) {
        notify(t("results.scenarios.scenario_expired"), "error", 5000);
      } else if (status === 404) {
        notify(t("results.scenarios.scenario_not_found"), "error", 5000);
      } else {
        notify(t("results.scenarios.invalid_scenario_string"), "error", 5000);
      }
      setBusy(false);
      setLoading(false);
    }
  }, [applyPayload, t, notify]);

  // Called by the view when PLs finish refreshing (clears busy/loading)
  const onRefreshComplete = useCallback(() => {
    if (loading) {
      setBusy(false);
      setLoading(false);
      notify(t("results.scenarios.scenario_loaded"), "success", 4000);
    }
  }, [loading, t, notify]);

  // Save or delete a scenario via backend
  const saveOrDeleteScenario = useCallback(
    (action, name, id) => {
      const params = {
        action,
        name,
        id,
        scenario_data: "",
        view_type: viewType,
      };
      if (action === "save" || action === "save_as") {
        params.scenario_data = JSON.stringify(buildPayload());
      }
      client
        .post("api/results_pl_explorer_save_delete_scenario/", params)
        .then((response) => {
          if (response.data.success) {
            const newScenarios = scenarios.filter(
              (s) =>
                s.name.toLowerCase() !== name.toLowerCase() ||
                s.view_type !== viewType,
            );
            if (action === "save" || action === "save_as") {
              newScenarios.push({
                name,
                id: response.data.id,
                hash: response.data.hash,
                view_type: viewType,
                scenario_data: params.scenario_data,
              });
              setScenarios(newScenarios);
              setCurrentScenario({
                name,
                id: response.data.id,
                hash: response.data.hash,
              });
              setSaveMode("save");
              notify(t("results.scenarios.scenario_saved"));
            } else {
              setScenarios(newScenarios);
              setCurrentScenario(null);
              setSaveMode("");
              notify(t("results.scenarios.scenario_deleted"));
            }
            setNameModalOpen(false);
          } else {
            notify(
              t("results.scenarios." + response.data.error),
              "error",
              5000,
            );
          }
          setBusy(false);
        })
        .catch(() => {
          notify(t("results.scenarios.scenario_save_error"), "error", 5000);
          setBusy(false);
        });
    },
    [scenarios, viewType, buildPayload, t, notify],
  );

  // Share the current view
  const shareView = useCallback(async () => {
    setShareLoading(true);
    setShareModalOpen(true);
    setShareURL("");
    try {
      const response = await client.post("api/scenarios/share/", {
        scenario_data: JSON.stringify(buildPayload()),
        view_type: viewType,
      });
      const hash = response.data.hash;
      const baseURL = window.location.href.split(/[?#]/)[0];
      setShareURL(`${baseURL}?scenario=${hash}`);
    } catch {
      notify(t("results.scenarios.scenario_save_error"), "error", 5000);
      setShareModalOpen(false);
    }
    setShareLoading(false);
  }, [buildPayload, viewType, t, notify]);

  const copyShareURL = useCallback(() => {
    if (shareURL) {
      navigator.clipboard.writeText(shareURL);
    }
  }, [shareURL]);

  // Build dropdown options
  const getDropdownOptions = useCallback(() => {
    const viewScenarios = filteredScenarios();
    const options = [];
    if (viewScenarios.length > 0) {
      options.push({
        explainer: true,
        text: t("results.scenarios.saved_scenarios"),
      });
      orderedScenarios(viewScenarios).forEach((scenario) => {
        options.push({
          identified: scenario.id,
          text: scenario.name,
          active: currentScenario && scenario.name === currentScenario.name,
        });
      });
    } else {
      options.push({
        explainer: true,
        text: t("results.scenarios.no_scenarios_saved"),
      });
    }
    options.push({ divider: true });
    options.push({
      identified: "action_save_scenario",
      text: t("results.scenarios.save_scenario"),
    });
    if (currentScenario) {
      options.push({
        identified: "action_save_scenario_as",
        text: t("results.scenarios.save_scenario_as"),
      });
      options.push({
        identified: "action_delete_scenario",
        text: t("results.scenarios.delete_scenario"),
      });
    } else {
      options.push({
        disabled: true,
        text: t("results.scenarios.save_scenario_as"),
      });
      options.push({
        disabled: true,
        text: t("results.scenarios.delete_scenario"),
      });
    }
    options.push({ divider: true });
    options.push({
      identified: "action_share_view",
      text: t("results.scenarios.share_view"),
    });
    return options;
  }, [filteredScenarios, currentScenario, t]);

  // Handle dropdown item selection
  const handleSelection = useCallback(
    (id) => {
      if (id === "action_save_scenario") {
        setSaveMode("save");
        if (currentScenario) {
          setBusy(true);
          saveOrDeleteScenario(
            "save",
            currentScenario.name,
            currentScenario.id,
          );
        } else {
          setNameModalInput("");
          setNameModalOpen(true);
          if (nameInputRef.current) {
            setTimeout(() => nameInputRef.current.firstChild?.focus(), 100);
          }
        }
      } else if (id === "action_save_scenario_as") {
        setSaveMode("save_as");
        setNameModalInput("");
        setNameModalOpen(true);
        if (nameInputRef.current) {
          setTimeout(() => nameInputRef.current.firstChild?.focus(), 100);
        }
      } else if (id === "action_delete_scenario") {
        if (currentScenario) {
          setBusy(true);
          saveOrDeleteScenario(
            "delete",
            currentScenario.name,
            currentScenario.id,
          );
        }
      } else if (id === "action_share_view") {
        shareView();
      } else {
        // Load saved scenario
        const viewScenarios = filteredScenarios();
        let scn, data;
        viewScenarios.forEach((scenario) => {
          if (scenario.id === id) {
            scn = scenario;
            try {
              data = JSON.parse(scenario.scenario_data);
            } catch {
              data = null;
            }
          }
        });
        if (!data) {
          notify(t("results.scenarios.invalid_scenario_string"), "error", 5000);
          return;
        }
        setBusy(true);
        setLoading(true);
        applyPayload(data);
        setCurrentScenario(scn);
      }
    },
    [
      currentScenario,
      filteredScenarios,
      saveOrDeleteScenario,
      shareView,
      applyPayload,
      t,
      notify,
    ],
  );

  // Handle name modal input change
  const handleNameChange = useCallback((event) => {
    setNameModalInput(event.target.value);
  }, []);

  // Handle name modal apply (save or save_as)
  const handleNameApply = useCallback(() => {
    const name = stripText(nameModalInput);
    setNameModalInput(name);
    if (name === "") return;
    const viewScenarios = filteredScenarios();
    const duplicate = viewScenarios.some(
      (s) => s.name.toLowerCase() === name.toLowerCase(),
    );
    if (duplicate) {
      notify(t("results.scenarios.scenario_already_exists"), "error", 5000);
      return;
    }
    setBusy(true);
    saveOrDeleteScenario(saveMode, name, currentScenario?.id);
  }, [
    nameModalInput,
    filteredScenarios,
    saveMode,
    currentScenario,
    saveOrDeleteScenario,
    t,
    notify,
  ]);

  return {
    scenarios: filteredScenarios(),
    currentScenario,
    busy,
    loading,

    setScenariosFromMeta,
    initFromURL,
    onRefreshComplete,

    saveOrDeleteScenario,
    shareView,

    getDropdownOptions,
    handleSelection,

    nameModalOpen,
    setNameModalOpen,
    nameModalInput,
    saveMode,
    handleNameChange,
    handleNameApply,
    nameInputRef,

    shareModalOpen,
    setShareModalOpen,
    shareURL,
    shareLoading,
    copyShareURL,
  };
}
